import React from 'react'
import * as Sentry from '@sentry/react'
import { Button } from '@/components/ui/button'

/**
 * Standard error fallback UI component
 */
const StandardErrorFallback: React.FC<{
  error: Error
  componentStack: string | null
  resetError: () => void
}> = ({ error, componentStack, resetError }) => (
  <div className="p-6 bg-red-50 border border-red-200 rounded-md">
    <h2 className="text-xl font-semibold text-red-700 mb-2">Something went wrong</h2>
    <p className="text-red-600 mb-4">
      The application encountered an unexpected error. Please try refreshing the page.
    </p>
    <div className="bg-card p-3 rounded border border-red-100 overflow-auto max-h-40">
      <p className="font-mono text-sm text-red-800">
        {error ? String(error) : 'An unknown error occurred'}
      </p>
      {componentStack && (
        <pre className="mt-2 text-xs text-muted-foreground overflow-auto">{componentStack}</pre>
      )}
    </div>
    <Button onClick={resetError} variant="destructive" className="mt-4">
      Try Again
    </Button>
  </div>
)

/**
 * Enhanced Sentry Error Boundary with standardized error UI
 * This component catches errors in its child component tree
 * and displays a consistent fallback UI instead of crashing the whole app
 */
export const ErrorBoundary: React.FC<{
  children: React.ReactNode
}> = ({ children }) => (
  <Sentry.ErrorBoundary
    fallback={({ error, componentStack, resetError }) => (
      <StandardErrorFallback
        error={error instanceof Error ? error : new Error(String(error))}
        componentStack={componentStack}
        resetError={resetError}
      />
    )}
  >
    {children}
  </Sentry.ErrorBoundary>
)
